import type { NeckDiagram } from "@wmf/shared/neckDiagrams";
import { useContext, useEffect, useRef, useState } from "react";
import { NeckDiagramStudentContext, useNeckDiagramPlayback } from "./NeckDiagramViewing.js";
let context: AudioContext | null = null;
let cancelActive: (() => void) | null = null;
/** Resume in the gesture, before loading the sound module (including on iPad). */
function unlockAudio() {
  const Constructor = window.AudioContext ?? (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Constructor) throw new Error("This browser does not support note playback.");
  if (!context || context.state === "closed") context = new Constructor();
  const ready = context.state === "running" ? Promise.resolve() : context.resume();
  return { context, ready };
}
export function useDiagramPlayer(diagram: NeckDiagram) {
  const enabled = useNeckDiagramPlayback();
  const { studentId } = useContext(NeckDiagramStudentContext);
  const [playing, setPlaying] = useState(false), [problem, setProblem] = useState<string | null>(null), [bpm, setBpm] = useState(80);
  const generation = useRef(0), cancel = useRef<(() => void) | null>(null);
  function stop() { generation.current++; cancel.current?.(); cancel.current = null; setPlaying(false); }
  useEffect(() => {
    setPlaying(false);
    const leave = () => { if (document.hidden) stop(); };
    document.addEventListener("visibilitychange", leave);
    window.addEventListener("pagehide", stop);
    return () => { generation.current++; cancel.current?.(); document.removeEventListener("visibilitychange", leave); window.removeEventListener("pagehide", stop); };
    // Any change invalidates a scheduled performance of the previous diagram.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [diagram, enabled, studentId]);
  async function play(dot?: { string: number; fret: number }) {
    if (!enabled) return;
    stop(); cancelActive?.(); setProblem(null);
    const ticket = ++generation.current;
    const cancelRequest = () => { generation.current++; cancel.current?.(); cancel.current = null; setPlaying(false); };
    cancelActive = cancelRequest;
    try {
      const audio = unlockAudio();
      setPlaying(true);
      const [sound] = await Promise.all([import("./neckDiagramAudio.js"), audio.ready]);
      if (generation.current !== ticket) return;
      const notes = dot ? sound.diagramNotes({ ...diagram, kind: "neck", dots: [dot], stringMarks: diagram.stringMarks.map(() => null) }, bpm).slice(0, 1) : sound.diagramNotes(diagram, bpm);
      if (!notes.length) { setPlaying(false); setProblem("Add notes or open strings to hear this diagram."); return; }
      cancel.current = sound.playDiagramNotes(audio.context, notes, () => { if (generation.current === ticket) { setPlaying(false); cancel.current = null; } });
    } catch (error) { if (generation.current === ticket) { setPlaying(false); setProblem(error instanceof Error ? error.message : "Could not play notes. Try Play again."); } }
  }
  return { enabled, playing, problem, bpm, setBpm, play, stop };
}
export function NeckDiagramPlayback({ player, chord }: { player: ReturnType<typeof useDiagramPlayer>; chord: boolean }) {
  if (!player.enabled) return null;
  return <div className="neck-playback">
    <button type="button" onClick={() => player.playing ? player.stop() : void player.play()}>{player.playing ? "Stop" : "Play"}</button>
    {!chord && <label>Tempo (bpm)<input type="number" min={40} max={240} step={1} value={player.bpm} onChange={event => player.setBpm(Math.max(40, Math.min(240, Number(event.target.value) || 80)))} /></label>}
    <span className="small muted">{chord ? "Strum, or tap a note to hear it." : "Up and down, or tap a note to hear it."}</span>
    {player.problem && <p role="alert" className="error">{player.problem}</p>}
  </div>;
}
